import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/smooth";
import { CONTACT } from "@/data/products";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { cls: "", parts: [{ t: "Quality ", red: true }, { t: "at a", red: false }] },
  { cls: "italic", parts: [{ t: "Fair ", red: false }, { t: "Price.", red: true }] },
];

const STATS = [
  { id: "range", big: "Multi-category", small: "Range Carried" },
  { id: "chennai", big: "Chennai", small: "Based Operations" },
  { id: "wholesale", big: "Wholesale", small: "Volume Orders Ready" },
  { id: "direct", big: "Direct Order", small: "On Call & WhatsApp" },
];

const SLIDES = [
  { img: "/slides/shirts.jpg", cat: "Men & Women Western Wear", label: "Shirts" },
  { img: "/slides/sarees.jpg", cat: "Sarees", label: "Printed Sarees" },
  { img: "/slides/uniforms.jpg", cat: "Uniform Fabrics", label: "Readymade Uniforms" },
  { img: "/slides/home-furnishing.jpg", cat: "Handloom & Home", label: "Home Furnishing" },
  { img: "/slides/lining-cloth.jpg", cat: "Running Materials", label: "Lining Cloth" },
];

const Hero = () => {
  const [slide, setSlide] = useState(0);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const mainX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const mainY = useTransform(sy, [-0.5, 0.5], [-10, 10]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 3200);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative overflow-hidden px-6 pb-14 pt-36 sm:px-12 sm:pt-44 lg:flex lg:min-h-screen lg:items-center lg:px-20 lg:pb-0 lg:pt-24"
    >
      <span aria-hidden="true" className="pointer-events-none absolute -right-10 top-16 select-none font-display text-[24vw] font-light leading-none text-crimson/[0.045]">
        1988
      </span>

      <div className="w-full">
      <div className="mx-auto grid w-full max-w-7xl items-start gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.15, duration: 0.8, ease: EASE }}
            className="mb-8 mt-8 flex items-center justify-center gap-4"
          >
            <span className="h-px w-12 bg-gold" />
            <span className="font-accent text-xs font-semibold tracking-[0.18em] text-gold">
              Wholesale Trading · Chennai · Estd 1988
            </span>
            <span className="h-px w-12 bg-gold" />
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display text-5xl font-light leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-3">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 2.25 + i * 0.14, duration: 1.05, ease: EASE }}
                >
                  {l.parts.map((p) =>
                    p.red ? (
                      <span key={p.t} className="text-crimson">{p.t}</span>
                    ) : (
                      p.t
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.75, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-clay sm:text-lg"
          >
            Praveen Textiles supplies a wide range of textiles &amp; readymade goods to
            wholesalers, retailers and garment manufacturers — built on fair rates, a diverse
            product range and a straight line to whoever's placing the order.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.9, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-btn"
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 rounded-full bg-[#1FA855] px-7 py-4 font-body text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#25D366]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Chat on WhatsApp
            </a>
            <button
              data-testid="view-products-btn"
              onClick={() => scrollToSection("#products")}
              className="group flex items-center gap-2 rounded-full border border-ink/25 px-7 py-4 font-body text-sm font-semibold text-ink transition-[border-color,background-color] duration-300 hover:border-gold hover:bg-gold/10"
            >
              View Product Range
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.2, duration: 1 }}
            className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-clay/80"
          >
            GST 33AFWPJ6755N1ZK · IEC AFWPJ6755N
          </motion.p>
        </div>

        <div className="relative min-w-0 lg:col-span-5 lg:mt-8">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 2.5, duration: 1.2, ease: EASE }}
            className="relative mx-auto aspect-[9/12] w-[min(9cm,100%)] overflow-hidden lg:ml-auto lg:mr-0"
          >
            <AnimatePresence>
              <motion.img
                key={slide}
                data-testid="hero-image-main"
                src={SLIDES[slide].img}
                alt={`${SLIDES[slide].label} — ${SLIDES[slide].cat}`}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1.1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
                style={{ x: mainX, y: mainY }}
                className="absolute inset-0 h-full w-full object-cover brightness-110"
              />
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-crimson/20 via-transparent to-transparent" />
            <div data-testid="hero-slide-label" className="absolute bottom-3 left-3 z-10 flex items-center gap-2.5 bg-crimson/90 px-3.5 py-2 backdrop-blur-sm">
              <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.18em] text-parchment/85 sm:text-[11px]">{SLIDES[slide].cat}</span>
              <span className="whitespace-nowrap font-body text-xs font-medium text-parchment sm:text-sm">{SLIDES[slide].label}</span>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.05, duration: 0.9, ease: EASE }}
        className="mx-auto mt-16 grid max-w-3xl grid-cols-2 gap-x-6 gap-y-7 border-t border-ink/10 pt-8 sm:grid-cols-4"
      >
        {STATS.map((s) => (
          <div key={s.id} data-testid={`hero-stat-${s.id}`} className="text-center">
            <p className="font-display text-xl font-semibold tracking-tight text-ink sm:text-2xl">{s.big}</p>
            <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-clay">{s.small}</p>
          </div>
        ))}
      </motion.div>

        <motion.button
          data-testid="scroll-cue"
          onClick={() => scrollToSection("#about")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.5, duration: 1 }}
          className="mx-auto mt-14 hidden flex-col items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] text-clay lg:flex"
          aria-label="Scroll to about"
        >
          Scroll
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
            <ArrowDown size={13} />
          </motion.span>
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
